import { memo } from "react";

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

import { MayaSvgAvatar } from "./maya-svg-avatar";
import { OrbitParticles } from "./orbit-particles";

interface MayaOrbProps {
  /** Diameter in px. */
  size: number;
  /** Thinking spins the aurora ring up and adds a glow. */
  thinking?: boolean;
  /** Sparkles orbiting the orb — for the large empty-state orb only. */
  particles?: boolean;
}

/**
 * Maya's presence: the avatar inside a spinning aurora ring. The ring idles
 * slowly and spins up while a reply is being written, so the header and the
 * thinking row show what Maya is doing without any text.
 */
export const MayaOrb = memo(
  ({ size, thinking = false, particles = false }: MayaOrbProps) => {
    const reducedMotion = usePrefersReducedMotion();
    return (
      <span
        aria-hidden="true"
        data-state={thinking ? "thinking" : "idle"}
        className="maya-orb relative grid shrink-0 place-items-center"
        style={{ width: size, height: size }}
      >
        {particles && (
          // The orbits are sized for the 60px launcher; scale them out so the
          // sparkles circle this orb instead of hiding behind it.
          <span
            className="pointer-events-none absolute inset-0"
            style={{ scale: String(size / 40) }}
          >
            <OrbitParticles enabled={!reducedMotion} />
          </span>
        )}
        <span className="maya-aurora maya-orb-ring" />
        <span
          className="absolute grid place-items-center rounded-full bg-surface"
          style={{ inset: Math.max(2, Math.round(size / 24)) }}
        >
          <MayaSvgAvatar className="h-[72%] w-[72%]" />
        </span>
      </span>
    );
  },
);

MayaOrb.displayName = "MayaOrb";
